import { useCallback } from "react";

import useArticleSection from "@FillInTableContainer/hooks/useArticleSection";
import { useEntryDataState } from "@stores/entryData";
import { getPressIdFromURL } from "@utils/getStateFromURL";

import type { ArticleEntryData } from "@/types/articleEntryData.types";
import type {
  DrugProfileValue,
  EntryDataValueAndOptions,
  EntryDataValueInfo,
} from "@/types/drugProfile.types";

interface UseHandleGroupShellReturn {
  handleAddDrugProfileGroupShell: (value: string) => void;
  isDuplicateValue: (value: string) => boolean;
}

interface Props {
  positionPropsInfo: {
    groupTableKey: "Drug Profile";
    groupTableIndex: number;
    groupShellKey: string | null;
    groupShellIndex: number | null;
    tableKey: string | null;
    tableIndex: number;
  };
}

export const useHandleDrugProfileGroupShell = ({
  positionPropsInfo,
}: Props): UseHandleGroupShellReturn => {
  const { groupShellKey, tableIndex } = positionPropsInfo;

  const articleSection = useArticleSection();
  const [entryData, setEntryData] = useEntryDataState(getPressIdFromURL());

  const isDuplicateValue = useCallback(
    (value: string) => {
      if (!entryData || !groupShellKey) return false;

      const drugProfileGroupShell = entryData[articleSection]?.[
        "Drug Profile"
      ] as DrugProfileValue[];

      if (!drugProfileGroupShell) return false;

      const shell = drugProfileGroupShell[tableIndex];
      if (!shell) return false;

      const key = groupShellKey as keyof DrugProfileValue;
      const currentValue = shell[key] as EntryDataValueAndOptions;

      if (!currentValue?.value) return false;

      if (groupShellKey === "INN Name" && currentValue.value.length >= 3) {
        return true;
      }

      const normalizedValue = value.toLowerCase().replace(/\s+/g, "");

      return currentValue.value.some((v) => {
        const existingValue = typeof v === "string" ? v : v.text;
        const normalizedExistingValue = existingValue
          .toLowerCase()
          .replace(/\s+/g, "");
        return normalizedExistingValue === normalizedValue;
      });
    },
    [entryData, groupShellKey, articleSection, tableIndex],
  );

  const handleAddDrugProfileGroupShell = useCallback(
    (value: string) => {
      if (!entryData) return;

      const updatedEntryData = { ...entryData } as ArticleEntryData;
      const drugProfileGroupShell = updatedEntryData[articleSection]?.[
        "Drug Profile"
      ] as DrugProfileValue[];

      if (drugProfileGroupShell && groupShellKey) {
        const updatedShells = drugProfileGroupShell.map((shell, index) => {
          if (index === tableIndex) {
            const key = groupShellKey as keyof DrugProfileValue;
            const currentValue = shell[key] as EntryDataValueAndOptions;

            if (currentValue) {
              if (isDuplicateValue(value)) {
                return shell;
              }

              if (groupShellKey === "Generic / Biosimilar") {
                return {
                  ...shell,
                  [key]: {
                    ...currentValue,
                    value: [value === "true" ? "true" : "false"],
                    options: currentValue.options || [],
                  } as any,
                };
              }

              const newValue: EntryDataValueInfo = {
                text: `${value}`,
                is_modified: true,
              };

              return {
                ...shell,
                [key]: {
                  ...currentValue,
                  value: [...(currentValue.value || []), newValue],
                  options: currentValue.options || [],
                } as EntryDataValueAndOptions,
              };
            }
          }
          return shell;
        });

        (updatedEntryData[articleSection] as any) = {
          ...updatedEntryData[articleSection],
          "Drug Profile": updatedShells,
        };
      }

      setEntryData(updatedEntryData);
    },
    [
      entryData,
      setEntryData,
      groupShellKey,
      articleSection,
      tableIndex,
      isDuplicateValue,
    ],
  );

  return {
    handleAddDrugProfileGroupShell,
    isDuplicateValue,
  };
};
